import { morphSection } from '@theme/section-renderer';

(() => {
  'use strict';

  const state = {
    initialised: false,
    adding: false,
    observer: null,
    openMenu: null,
  };

  const SELECTORS = {
    bundle: '[data-bundle-save]',
    option: '[data-bundle-option]',
    trigger: '[data-bundle-trigger]',
    menu: '[data-bundle-menu]',
    product: '[data-bundle-product]',
    selected: '[data-bundle-selected]',
    complete: '[data-bundle-complete]',
    message: '[data-bundle-message]',
    dismiss: '[data-bundle-dismiss]',
    cartItems: 'cart-items-component',
  };

  /* =========================================================
     GET BUNDLE
     ========================================================= */

  function getBundle() {
    return document.querySelector(SELECTORS.bundle);
  }

  function getOptions(bundle) {
    if (!bundle) return [];

    return [...bundle.querySelectorAll(SELECTORS.option)]
      .filter((option) => !option.hidden);
  }

  /* =========================================================
     GET HORIZON CART SECTION ID
     
     IMPORTANT:
     We DO NOT hard-code cart-drawer-section.
     
     Horizon stores the actual section ID on:
     
     <cart-items-component data-section-id="...">
     ========================================================= */

  function getCartSectionId() {
    const cartItems =
      document.querySelector(
        `${SELECTORS.cartItems}[data-drawer]`
      ) ||
      document.querySelector(
        SELECTORS.cartItems
      );

    if (!cartItems) {
      console.warn(
        'Mateevs Bundle: cart-items-component not found.'
      );

      return null;
    }

    const sectionId =
      cartItems.dataset.sectionId;

    if (!sectionId) {
      console.warn(
        'Mateevs Bundle: cart section ID not found.'
      );

      return null;
    }

    return sectionId;
  }

  /* =========================================================
     CLOSE MENUS
     ========================================================= */

  function closeAllMenus() {
    document.querySelectorAll(SELECTORS.menu).forEach((menu) => {
      menu.hidden = true;

      const trigger = menu
        .closest(SELECTORS.option)
        ?.querySelector(SELECTORS.trigger);

      if (trigger) {
        trigger.setAttribute(
          'aria-expanded',
          'false'
        );
      }
    });

    state.openMenu = null;
  }

  /* =========================================================
     OPEN / CLOSE DROPDOWN
     ========================================================= */

  function toggleMenu(trigger) {
    const option =
      trigger.closest(
        SELECTORS.option
      );

    if (!option) return;

    const menu =
      option.querySelector(
        SELECTORS.menu
      );

    if (!menu) return;

    const isOpen =
      !menu.hidden;

    closeAllMenus();

    if (!isOpen) {
      menu.hidden = false;

      trigger.setAttribute(
        'aria-expanded',
        'true'
      );

      state.openMenu =
        menu;
    }
  }

  /* =========================================================
     UPDATE BUTTON
     ========================================================= */

  function updateButton(bundle) {
    if (!bundle) return;

    const options =
      getOptions(bundle);

    const button =
      bundle.querySelector(
        SELECTORS.complete
      );

    if (!button) return;

    const allSelected =
      options.length > 0 &&
      options.every(
        (option) =>
          option.dataset.selected ===
          'true'
      );

    button.disabled =
      !allSelected;
  }

  /* =========================================================
     SELECT PRODUCT
     ========================================================= */

  function selectProduct(
    option,
    item
  ) {
    if (!option || !item) return;

    const selected =
      option.querySelector(
        SELECTORS.selected
      );

    if (!selected) return;

    const productTitle =
      item.dataset.productTitle ||
      '';

    const variantTitle =
      item.dataset.variantTitle ||
      '';

    const variantId =
      item.dataset.variantId ||
      '';

    const productId =
      item.dataset.productId ||
      '';

    let displayText =
      productTitle;

    if (variantTitle) {
      displayText +=
        ` — ${variantTitle}`;
    }

    selected.textContent =
      displayText;

    option.dataset.selected =
      'true';

    option.dataset.variantId =
      variantId;

    option.dataset.productId =
      productId;

    option.classList.add(
      'is-selected'
    );

    const menu =
      option.querySelector(
        SELECTORS.menu
      );

    const trigger =
      option.querySelector(
        SELECTORS.trigger
      );

    if (menu) {
      menu.hidden =
        true;
    }

    if (trigger) {
      trigger.setAttribute(
        'aria-expanded',
        'false'
      );
    }

    state.openMenu =
      null;

    const bundle =
      option.closest(
        SELECTORS.bundle
      );

    updateButton(bundle);
  }

  /* =========================================================
     MESSAGE
     ========================================================= */

  function showMessage(
    bundle,
    message
  ) {
    if (!bundle) return;

    const element =
      bundle.querySelector(
        SELECTORS.message
      );

    if (element) {
      element.textContent =
        message || '';
    }
  }

  /* =========================================================
     GET CART
     ========================================================= */

  async function getCart() {
    const cartUrl =
      window.Shopify?.routes?.root
        ? `${window.Shopify.routes.root}cart.js`
        : '/cart.js';

    const response =
      await fetch(
        cartUrl,
        {
          method: 'GET',

          headers: {
            Accept:
              'application/json',
          },

          credentials:
            'same-origin',

          cache:
            'no-store',
        }
      );

    if (!response.ok) {
      throw new Error(
        'Unable to retrieve cart.'
      );
    }

    return response.json();
  }

  /* =========================================================
     REFRESH BUNDLE STATE
     ========================================================= */

  async function refreshBundleState() {
    const bundle =
      getBundle();

    if (!bundle) return;

    try {
      const cart =
        await getCart();

      const count =
        Number(
          cart.item_count || 0
        );

      bundle.dataset.cartCount =
        String(count);

      /*
       * If 3 or more products exist,
       * let Liquid/server-rendered HTML
       * control the complete state.
       */

      if (count >= 3) {
        return;
      }

      const needed =
        Math.max(
          0,
          3 - count
        );

      const options = [
        ...bundle.querySelectorAll(
          SELECTORS.option
        ),
      ];

      options.forEach(
        (
          option,
          index
        ) => {
          option.hidden =
            index >= needed;
        }
      );

      const intro =
        bundle.querySelector(
          '[data-bundle-intro]'
        );

      if (intro) {
        intro.textContent =
          needed === 1
            ? 'Add 1 more fragrance to save 25%'
            : `Add ${needed} more fragrances to save 25%`;
      }

      updateButton(
        bundle
      );

    } catch (error) {
      console.error(
        'Mateevs Bundle state sync error:',
        error
      );
    }
  }

  /* =========================================================
     REFRESH HORIZON CART
     
     Uses the ACTUAL section ID from Horizon.
     ========================================================= */

  async function refreshHorizonCart(
    responseData,
    sectionId
  ) {
    if (!sectionId) {
      throw new Error(
        'Cart section ID could not be found.'
      );
    }

    /*
     * Shopify returns sections like:
     *
     * {
     *   "sections": {
     *      "some-real-section-id": "...."
     *   }
     * }
     */

    const sectionHtml =
      responseData?.sections?.[
        sectionId
      ];

    if (!sectionHtml) {

      console.error(
        'Mateevs Bundle: section was not returned.',
        {
          requestedSection:
            sectionId,

          returnedSections:
            Object.keys(
              responseData?.sections ||
              {}
            ),
        }
      );

      throw new Error(
        'Shopify did not return the cart drawer section.'
      );
    }

    /*
     * Horizon's own cart components use
     * morphSection() to replace their section.
     */

    await morphSection(
      sectionId,
      sectionHtml,
      {
        mode:
          'hydratable',

        injectStylesheet:
          false,
      }
    );

    /*
     * Close any open dropdown.
     */

    closeAllMenus();

    /*
     * Wait for the DOM to settle.
     */

    await new Promise(
      (resolve) =>
        requestAnimationFrame(
          () =>
            requestAnimationFrame(
              resolve
            )
        )
    );
  }

  /* =========================================================
     ADD BUNDLE
     ========================================================= */

  async function addBundle() {
    const bundle =
      getBundle();

    if (
      !bundle ||
      state.adding
    ) {
      return;
    }

    const options =
      getOptions(
        bundle
      );

    const variantIds =
      options
        .map(
          (option) =>
            option.dataset
              .variantId
        )
        .filter(
          Boolean
        );

    /*
     * Every visible option must
     * contain a selected product.
     */

    if (
      variantIds.length !==
      options.length
    ) {
      showMessage(
        bundle,
        'Please select a fragrance for each option.'
      );

      return;
    }

    /*
     * Get Horizon's REAL section ID
     * BEFORE adding products.
     */

    const sectionId =
      getCartSectionId();

    if (!sectionId) {

      showMessage(
        bundle,
        'Unable to update the cart. Please refresh the page and try again.'
      );

      return;
    }

    state.adding =
      true;

    const button =
      bundle.querySelector(
        SELECTORS.complete
      );

    if (button) {

      button.disabled =
        true;

      button.textContent =
        'ADDING...';
    }

    showMessage(
      bundle,
      ''
    );

    try {

      /* =====================================================
         ADD PRODUCTS
         ===================================================== */

      const addUrl =
        window.Shopify?.routes?.root
          ? `${window.Shopify.routes.root}cart/add.js`
          : '/cart/add.js';

      const response =
        await fetch(
          addUrl,
          {
            method:
              'POST',

            headers: {
              'Content-Type':
                'application/json',

              Accept:
                'application/json',

              'X-Requested-With':
                'XMLHttpRequest',
            },

            credentials:
              'same-origin',

            body:
              JSON.stringify({

                items:
                  variantIds.map(
                    (id) => ({
                      id:
                        Number(id),

                      quantity:
                        1,
                    })
                  ),

                /*
                 * THIS IS NOW THE REAL
                 * HORIZON SECTION ID.
                 */

                sections:
                  [sectionId],

                sections_url:
                  window.location
                    .pathname,
              }),
          }
        );

      const responseData =
        await response.json();

      if (!response.ok) {

        throw new Error(
          responseData?.description ||
          responseData?.message ||
          'Unable to add bundle.'
        );
      }

      /* =====================================================
         IMPORTANT
         
         Shopify has now:
         
         1. Added both products
         2. Calculated the cart
         3. Applied the automatic discount
         4. Rendered the requested Horizon section
         
         Now morph that section.
         ===================================================== */

      await refreshHorizonCart(
        responseData,
        sectionId
      );

      /*
       * Get the NEW bundle after Horizon
       * has replaced the cart section.
       */

      const newBundle =
        getBundle();

      /*
       * Get the actual cart count.
       */

      const cart =
        await getCart();

      const cartCount =
        Number(
          cart.item_count || 0
        );

      /*
       * Store current count.
       */

      if (newBundle) {

        newBundle.dataset.cartCount =
          String(
            cartCount
          );
      }

      /*
       * DO NOT manually insert
       * "BUNDLE COMPLETE".
       *
       * Liquid should have rendered it
       * based on the new cart.
       */

      /*
       * Make sure menus are closed.
       */

      closeAllMenus();

      state.adding =
        false;

    } catch (error) {

      console.error(
        'Mateevs Bundle add error:',
        error
      );

      const currentBundle =
        getBundle();

      if (currentBundle) {

        showMessage(
          currentBundle,
          error.message ||
            'Something went wrong. Please try again.'
        );

        const currentButton =
          currentBundle.querySelector(
            SELECTORS.complete
          );

        if (currentButton) {

          currentButton.disabled =
            false;

          currentButton.textContent =
            'ADD TO BUNDLE';
        }

        updateButton(
          currentBundle
        );
      }

      state.adding =
        false;
    }
  }

  /* =========================================================
     POINTER DOWN
     
     ONLY OPENS DROPDOWN.
     
     Does NOT select product.
     This preserves mobile scrolling.
     ========================================================= */

  function handlePointerDown(
    event
  ) {
    const trigger =
      event.target.closest?.(
        SELECTORS.trigger
      );

    if (!trigger) return;

    const bundle =
      trigger.closest(
        SELECTORS.bundle
      );

    if (!bundle) return;

    event.preventDefault();

    event.stopPropagation();

    toggleMenu(
      trigger
    );
  }

  /* =========================================================
     PRODUCT CLICK
     ========================================================= */

  function handleProductClick(
    event
  ) {
    const item =
      event.target.closest?.(
        SELECTORS.product
      );

    if (!item) return;

    const option =
      item.closest(
        SELECTORS.option
      );

    if (!option) return;

    event.preventDefault();

    event.stopPropagation();

    selectProduct(
      option,
      item
    );
  }

  /* =========================================================
     ADD BUTTON CLICK
     ========================================================= */

  function handleCompleteClick(
    event
  ) {
    const button =
      event.target.closest?.(
        SELECTORS.complete
      );

    if (!button) return;

    event.preventDefault();

    event.stopPropagation();

    if (
      button.disabled
    ) {
      return;
    }

    addBundle();
  }

  /* =========================================================
     NO THANKS
     ========================================================= */

  function handleDismiss(
    event
  ) {
    const button =
      event.target.closest?.(
        SELECTORS.dismiss
      );

    if (!button) return;

    event.preventDefault();

    event.stopPropagation();

    const bundle =
      button.closest(
        SELECTORS.bundle
      );

    if (!bundle) return;

    bundle.style.display =
      'none';

    try {

      sessionStorage.setItem(
        'mateevs_bundle_dismissed',
        'true'
      );

    } catch (error) {
      /*
       * Ignore storage errors.
       */
    }
  }

  /* =========================================================
     OUTSIDE CLICK
     ========================================================= */

  function handleOutsideClick(
    event
  ) {
    if (
      !state.openMenu
    ) {
      return;
    }

    if (
      state.openMenu.contains(
        event.target
      )
    ) {
      return;
    }

    const option =
      state.openMenu.closest(
        SELECTORS.option
      );

    if (
      option?.contains(
        event.target
      )
    ) {
      return;
    }

    closeAllMenus();
  }

  /* =========================================================
     KEYBOARD
     ========================================================= */

  function handleKeyDown(
    event
  ) {
    if (
      event.key ===
      'Escape'
    ) {
      closeAllMenus();
    }
  }

  /* =========================================================
     MUTATION OBSERVER
     ========================================================= */

  function observeCartDrawer() {

    if (
      !document.body ||
      state.observer
    ) {
      return;
    }

    state.observer =
      new MutationObserver(
        (mutations) => {

          let bundleFound =
            false;

          for (
            const mutation
            of mutations
          ) {

            if (
              mutation.type !==
              'childList'
            ) {
              continue;
            }

            for (
              const node
              of mutation.addedNodes
            ) {

              if (
                !(
                  node instanceof
                  HTMLElement
                )
              ) {
                continue;
              }

              if (
                node.matches?.(
                  SELECTORS.bundle
                ) ||
                node.querySelector?.(
                  SELECTORS.bundle
                )
              ) {

                bundleFound =
                  true;

                break;
              }
            }

            if (
              bundleFound
            ) {
              break;
            }
          }

          if (
            bundleFound
          ) {

            setTimeout(
              () => {
                refreshBundleState();
              },
              100
            );
          }
        }
      );

    state.observer.observe(
      document.body,
      {
        childList:
          true,

        subtree:
          true,
      }
    );
  }

  /* =========================================================
     INITIALISE
     ========================================================= */

  function init() {

    if (
      state.initialised
    ) {
      return;
    }

    state.initialised =
      true;

    /*
     * Dropdown trigger.
     */

    document.addEventListener(
      'pointerdown',
      handlePointerDown,
      true
    );

    /*
     * Product selection.
     */

    document.addEventListener(
      'click',
      handleProductClick,
      true
    );

    /*
     * Add bundle.
     */

    document.addEventListener(
      'click',
      handleCompleteClick,
      true
    );

    /*
     * No thanks.

     */

    document.addEventListener(
      'click',
      handleDismiss,
      true
    );

    /*
     * Outside click.
     */

    document.addEventListener(
      'click',
      handleOutsideClick
    );

    /*
     * Escape.

     */

    document.addEventListener(
      'keydown',
      handleKeyDown,
      true
    );

    /*
     * Watch Horizon
     * section morphing.
     */

    observeCartDrawer();

    /*
     * Initial bundle state.
     */

    if (
      getBundle()
    ) {

      setTimeout(
        () => {
          refreshBundleState();
        },
        100
      );
    }

    /*
     * Theme cart events.
     *
     * These only synchronise the
     * bundle controls.
     */

    document.addEventListener(
      'cart:update',
      () => {

        setTimeout(
          () => {
            refreshBundleState();
          },
          200
        );

      }
    );

    document.addEventListener(
      'cart:refresh',
      () => {

        setTimeout(
          () => {
            refreshBundleState();
          },
          200
        );

      }
    );

    /*
     * Debug API.
     */

    window.MateevsBundle = {

      refresh:
        refreshBundleState,

      getBundle,

      getCartSectionId,

      closeMenus:
        closeAllMenus,

    };
  }

  /* =========================================================
     START
     ========================================================= */

  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      init,
      {
        once:
          true,
      }
    );

  } else {

    init();

  }

})();
